import { createBotLinks } from "@tg-gallery/deep-links"
import { Camera, Check, GripVertical, ImageIcon, Lock, Settings, Share2, Star } from "lucide-react"
import { observer } from "mobx-react-lite"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router"
import { match } from "ts-pattern"
import { mediaUrl } from "../lib/api.js"
import { getTelegramWebApp } from "../lib/telegram.js"
import { useStore, useT } from "../stores/StoreProvider.js"
import { GalleryGrid } from "./GalleryGrid.js"
import { GalleryMasonry } from "./GalleryMasonry.js"
import { Lightbox } from "./Lightbox.js"

function GalleryViewSkeleton() {
  return (
    <div>
      <div className="skeleton w-full h-[40vh] min-h-[200px] rounded-none" />
      <div className="p-4 space-y-2">
        <div className="skeleton h-6 w-2/3 rounded" />
        <div className="skeleton h-4 w-1/3 rounded" />
      </div>
      <div className="grid grid-cols-3 gap-[2px] px-[2px]">
        {Array.from({ length: 9 }).map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton placeholders
          <div key={i} className="skeleton aspect-square rounded-none" />
        ))}
      </div>
    </div>
  )
}

function handleShare(gallerySlug: string) {
  const webDomain = import.meta.env.VITE_WEB_DOMAIN ?? window.location.origin
  const shareUrl = `${webDomain}/g/${gallerySlug}`
  const webapp = getTelegramWebApp()
  if (webapp) {
    webapp.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`)
  } else {
    void navigator.clipboard.writeText(shareUrl)
  }
}

interface ShareButtonProps {
  gallerySlug: string
  variant: "cover" | "plain"
}

function ShareButton({ gallerySlug, variant }: ShareButtonProps) {
  const coverClass =
    "w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center btn-press"
  const plainClass =
    "w-9 h-9 rounded-full bg-tg-secondary flex items-center justify-center btn-press"

  return (
    <button
      onClick={() => handleShare(gallerySlug)}
      className={variant === "cover" ? coverClass : plainClass}
      type="button"
    >
      <Share2 className={variant === "cover" ? "w-4 h-4 text-white" : "w-4 h-4 text-tg-hint"} />
    </button>
  )
}

interface GalleryLoadedViewProps {
  gallery: {
    id: string
    slug: string
    title: string
    description: string | null
    coverMediaId: string | null
    ownerUserId: string
  }
  media: { id: string }[]
  isOwner: boolean
  layoutType: string
}

const GalleryLoadedView = observer(function GalleryLoadedView({
  gallery,
  media,
  isOwner,
  layoutType,
}: GalleryLoadedViewProps) {
  const { viewerStore } = useStore()
  const t = useT()
  const navigate = useNavigate()
  const mediaIds = media.map((m) => m.id)
  const [editMode, setEditMode] = useState(false)

  // Handle pending deep link media
  useEffect(() => {
    if (viewerStore.pendingMediaId && media.length > 0) {
      const idx = media.findIndex((m) => m.id === viewerStore.pendingMediaId)
      if (idx >= 0) {
        viewerStore.open(mediaIds, idx)
      }
      viewerStore.pendingMediaId = null
    }
  }, [media, viewerStore.pendingMediaId, viewerStore, mediaIds])

  return (
    <div className="page-enter">
      {/* Cover */}
      {gallery.coverMediaId ? (
        <div className="cover-fullbleed cover-sticky">
          <img src={mediaUrl(gallery.coverMediaId)} alt="" />
          <div className="cover-overlay">
            <div className="flex items-end justify-between">
              <div>
                <h1 className="text-2xl font-bold mb-1">{gallery.title}</h1>
                {gallery.description && <p className="opacity-80 text-sm">{gallery.description}</p>}
                <p className="opacity-60 text-xs mt-2 flex items-center gap-1">
                  <ImageIcon className="w-3 h-3" />
                  {media.length} {t("gallery.photos_count")}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <ShareButton gallerySlug={gallery.slug} variant="cover" />
                {isOwner && (
                  <button
                    onClick={() => setEditMode(!editMode)}
                    className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center btn-press"
                    type="button"
                  >
                    {editMode ? (
                      <Check className="w-4 h-4 text-white" />
                    ) : (
                      <GripVertical className="w-4 h-4 text-white" />
                    )}
                  </button>
                )}
                {isOwner && (
                  <button
                    onClick={() => navigate(`/gallery/${gallery.id}/settings`)}
                    className="w-9 h-9 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center btn-press"
                    type="button"
                  >
                    <Settings className="w-4 h-4 text-white" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="px-4 pt-6 pb-4 flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-tg-text">{gallery.title}</h1>
            {gallery.description && (
              <p className="text-tg-text-secondary text-sm mt-1">{gallery.description}</p>
            )}
            <p className="text-tg-hint text-xs mt-2 flex items-center gap-1">
              <ImageIcon className="w-3 h-3" />
              {media.length} {t("gallery.photos_count")}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ShareButton gallerySlug={gallery.slug} variant="plain" />
            {isOwner && (
              <button
                onClick={() => setEditMode(!editMode)}
                className="w-9 h-9 rounded-full bg-tg-secondary flex items-center justify-center btn-press"
                type="button"
              >
                {editMode ? (
                  <Check className="w-4 h-4 text-tg-hint" />
                ) : (
                  <GripVertical className="w-4 h-4 text-tg-hint" />
                )}
              </button>
            )}
            {isOwner && (
              <button
                onClick={() => navigate(`/gallery/${gallery.id}/settings`)}
                className="w-9 h-9 rounded-full bg-tg-secondary flex items-center justify-center btn-press"
                type="button"
              >
                <Settings className="w-4 h-4 text-tg-hint" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Gallery layout */}
      {media.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center px-6">
          <div className="w-20 h-20 rounded-full bg-tg-accent/10 flex items-center justify-center mb-5">
            <Camera className="w-10 h-10 text-tg-accent/60" />
          </div>
          <p className="text-tg-text font-medium text-base mb-1">{t("gallery.no_photos")}</p>
          <p className="text-tg-hint text-sm max-w-[240px]">{t("gallery.send_photos_hint")}</p>
        </div>
      ) : layoutType === "masonry" ? (
        <GalleryMasonry mediaIds={mediaIds} />
      ) : (
        <GalleryGrid mediaIds={mediaIds} editMode={editMode} galleryId={gallery.id} />
      )}

      <Lightbox gallerySlug={gallery.slug} isOwner={isOwner} galleryId={gallery.id} />
    </div>
  )
})

export const GalleryView = observer(function GalleryView() {
  const { galleryStore, authStore } = useStore()
  const t = useT()
  const { themeStore } = useStore() as {
    themeStore: { layout: { type: string } }
  }
  const layoutType =
    (themeStore as { layout: { type: string } } | undefined)?.layout?.type ?? "grid"

  const isOwner =
    galleryStore.detailState.status === "loaded" &&
    authStore.userId === galleryStore.detailState.gallery.ownerUserId

  return match(galleryStore.detailState)
    .with({ status: "idle" }, () => null)
    .with({ status: "loading" }, () => <GalleryViewSkeleton />)
    .with({ status: "error" }, ({ message }) => (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <p className="text-tg-destructive text-sm">{message}</p>
      </div>
    ))
    .with({ status: "no_access" }, ({ gallery }) => (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        {gallery.coverMediaId ? (
          <img
            src={mediaUrl(gallery.coverMediaId)}
            alt=""
            className="w-24 h-24 rounded-xl object-cover mb-4 opacity-60"
          />
        ) : (
          <div className="w-24 h-24 rounded-xl bg-tg-secondary flex items-center justify-center mb-4">
            <Lock className="w-8 h-8 text-tg-hint" />
          </div>
        )}
        <h2 className="text-lg font-semibold text-tg-text mb-1">{gallery.title}</h2>
        {gallery.description && (
          <p className="text-tg-text-secondary text-sm mb-2">{gallery.description}</p>
        )}
        <p className="text-tg-hint text-sm mb-6">{t("gallery.requires_access")}</p>
        {gallery.pricingType === "one_time" && gallery.priceStars > 0 && (
          <button
            className="flex items-center gap-2 bg-tg-button text-tg-button-text px-8 py-3 rounded-xl font-semibold text-base active:opacity-80 transition-opacity"
            type="button"
            onClick={() => {
              const botUsername = (import.meta.env.VITE_TELEGRAM_BOT_USERNAME ?? "").replace("@", "")
              const webDomain = import.meta.env.VITE_WEB_DOMAIN ?? ""
              const botLinks = createBotLinks(botUsername, webDomain)
              const url = botLinks.purchaseGallery(gallery.id)
              const webapp = getTelegramWebApp()
              if (webapp) {
                webapp.openTelegramLink(url)
              } else {
                window.open(url, "_blank")
              }
            }}
          >
            <Star className="w-4 h-4" />
            {t("gallery.unlock_for")} {gallery.priceStars} Stars
          </button>
        )}
      </div>
    ))
    .with({ status: "loaded" }, ({ gallery, media }) => (
      <GalleryLoadedView
        gallery={gallery}
        media={media}
        isOwner={isOwner}
        layoutType={layoutType}
      />
    ))
    .exhaustive()
})
